/**
 * TemplateAnalyticsShell — shared chrome for V2.5-B /analytics/template/:name
 * pages.
 *
 * Presentational only. Each page owns its own data fetching, filter state,
 * and grid layout — the shell just renders masthead + sticky export/filter
 * bar + body slot. This keeps per-page concerns (polling, derived scores,
 * PII scrubbing) out of the shared layout per Gemini/Codex round-1
 * MEDIUM #1 ("keep the shell presentational").
 *
 * Composition:
 *   <TemplateAnalyticsShell
 *     title="Event post-mortem"
 *     eventName="NOS Events 2026"
 *     windowChip="May 1 – May 5"
 *     filters={<FilterBar ... />}
 *     actions={<ExportCsv ... />}
 *   >
 *     <Grid>
 *       <Pane .../>
 *     </Grid>
 *   </TemplateAnalyticsShell>
 *
 * The filter+action bar sticks to the top of the scroll container
 * at every breakpoint so export/filter controls stay reachable as
 * the operator scrolls long grids. Both slots are optional — a page
 * can ship no filters or no actions and the bar collapses to an
 * empty flex row (preserving the sticky offset).
 */

import type { ReactNode } from 'react'

export interface TemplateAnalyticsShellProps {
  /** Page title — rendered as h1. */
  title: string
  /** Optional operator-facing context line below title. */
  subtitle?: string
  /**
   * Event name chip displayed beside the title (e.g. "NOS Events 2026").
   * Omitted when no event is in focus.
   */
  eventName?: string | null
  /**
   * Event window chip (e.g. "May 1 – May 5", "live now"). Null suppresses.
   */
  windowChip?: string | null
  /** Filter controls rendered in the sticky bar LEFT slot. */
  filters?: ReactNode
  /** Action controls (Export, Share) rendered in the sticky bar RIGHT slot. */
  actions?: ReactNode
  /**
   * Optional banner above the grid — surface live-running / event-closed
   * notices that apply to the whole page.
   */
  banner?: ReactNode
  children: ReactNode
  'data-testid'?: string
}

export function TemplateAnalyticsShell({
  title,
  subtitle,
  eventName,
  windowChip,
  filters,
  actions,
  banner,
  children,
  'data-testid': testId = 'template-analytics-shell',
}: TemplateAnalyticsShellProps) {
  return (
    <div data-testid={testId} className="flex h-full flex-col overflow-y-auto">
      <header className="border-b border-hairline bg-white/80 px-6 py-4 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/60">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-700 dark:text-butler-200">
              Butler Amusements · Analytics
            </div>
            <h1 className="mt-1 font-display text-xl font-bold tracking-tight text-butler-800 dark:text-butler-100">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-0.5 text-sm text-ink-soft dark:text-slate-400">
                {subtitle}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {eventName ? (
              <span
                data-testid="template-event-chip"
                className="rounded-full border border-hairline bg-butler-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-butler-700 dark:border-butler-700 dark:bg-butler-900/40 dark:text-butler-200"
                title={eventName}
              >
                {eventName}
              </span>
            ) : null}
            {windowChip ? (
              <span
                data-testid="template-window-chip"
                className="rounded-full border border-hairline bg-surface-stripe px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
              >
                {windowChip}
              </span>
            ) : null}
          </div>
        </div>
      </header>
      {/* Sticky filter/export bar — stays at `top-0` of the scroll
          container at every breakpoint; each page's grid scrolls
          under it. */}
      <div
        data-testid="template-toolbar"
        className="sticky top-0 z-10 border-b border-hairline bg-white/90 px-6 py-2.5 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/80"
      >
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="template-filters-slot">
            {filters ?? null}
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="template-actions-slot">
            {actions ?? null}
          </div>
        </div>
      </div>
      {banner ? (
        <div className="px-6 pt-4">
          <div className="mx-auto w-full max-w-6xl">{banner}</div>
        </div>
      ) : null}
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-6">{children}</main>
    </div>
  )
}

/**
 * BannerNote — consistent amber info banner used by pages to surface
 * live-running / event-closed / data-pending states. Exported so pages
 * keep banner styling consistent without duplicating palette classes.
 */
export interface BannerNoteProps {
  tone?: 'info' | 'warning'
  children: ReactNode
  'data-testid'?: string
}

export function BannerNote({
  tone = 'info',
  children,
  'data-testid': testId,
}: BannerNoteProps) {
  const skin =
    tone === 'warning'
      ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-900/30 dark:text-rose-200'
      : 'border-glow-300 bg-glow-50 text-butler-700 dark:border-glow-700 dark:bg-glow-900/30 dark:text-glow-200'
  return (
    <div
      data-testid={testId ?? 'template-banner'}
      role="status"
      className={`flex items-start gap-2 rounded-card border px-3 py-2 text-sm ${skin}`}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current"
      />
      <div className="flex-1">{children}</div>
    </div>
  )
}

/**
 * DeferredPane — placeholder used by template pages for panes whose
 * data source is pending backend work (weather, demographics,
 * competing-events, breakage, customer-segments). Render this so the
 * page layout shape stays intact and the empty-state copy is
 * operator-facing rather than a broken error.
 */
export interface DeferredPaneProps {
  title: string
  /** Operator-facing copy — NO staging-flavor "will light up when endpoint mounted". */
  message: string
  'data-testid'?: string
}

export function DeferredPane({
  title,
  message,
  'data-testid': testId,
}: DeferredPaneProps) {
  return (
    <section
      data-testid={testId ?? `deferred-pane-${title.toLowerCase().replace(/\s+/g, '-')}`}
      data-state="deferred"
      className="flex min-h-[14rem] flex-col rounded-card border border-dashed border-hairline bg-surface-stripe p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800/60"
    >
      <div className="flex items-center gap-2">
        <div
          aria-hidden="true"
          className="inline-block h-1.5 w-1.5 rounded-full bg-butler-400"
        />
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-butler-700 dark:text-butler-200">
          {title}
        </h3>
      </div>
      <div className="mt-auto flex flex-1 items-end text-xs text-ink-soft dark:text-slate-400">
        {message}
      </div>
    </section>
  )
}
